/**
 * API client functions for MOTD
 */

export interface MotdChecksum {
	checksum: string;
	s3_url: string;
	title: string;
}

export interface MotdData {
	title: string;
	content: string;
}

/**
 * Fetch the checksum and S3 URL of the current MOTD
 * @returns MotdChecksum object or null if no MOTD exists
 */
export async function fetchMotdChecksum(): Promise<MotdChecksum | null> {
	try {
		const response = await fetch('/api/motd/checksum');
		if (!response.ok) {
			console.error('Failed to fetch MOTD checksum:', response.statusText);
			return null;
		}
		const data = await response.json();
		return data as MotdChecksum | null;
	} catch (error) {
		console.error('Error fetching MOTD checksum:', error);
		return null;
	}
}

/**
 * Fetch MOTD data from S3 URL
 * @param url S3 URL with cache-busting parameter
 * @returns MOTD data with title and content
 */
export async function fetchMotdContent(url: string): Promise<MotdData | null> {
	try {
		const response = await fetch(url);
		if (!response.ok) {
			console.error('Failed to fetch MOTD content from S3:', response.statusText);
			return null;
		}
		const data = await response.json();
		return data as MotdData;
	} catch (error) {
		console.error('Error fetching MOTD content:', error);
		return null;
	}
}

/**
 * Upload new MOTD content (superuser only)
 * @param title Custom title for the message
 * @param content Markdown content
 * @returns MotdChecksum object with new checksum and URL
 */
export async function uploadMotd(title: string, content: string): Promise<MotdChecksum | null> {
	try {
		const response = await fetch('/api/motd/', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ title, content })
		});

		if (!response.ok) {
			const error = await response.json();
			console.error('Failed to upload MOTD:', error);
			return null;
		}

		return (await response.json()) as MotdChecksum;
	} catch (error) {
		console.error('Error uploading MOTD:', error);
		return null;
	}
}

/**
 * Delete the current MOTD (superuser only)
 * @returns true if successful
 */
export async function deleteMotd(): Promise<boolean> {
	try {
		const response = await fetch('/api/motd/', {
			method: 'DELETE'
		});

		if (!response.ok) {
			console.error('Failed to delete MOTD:', response.statusText);
			return false;
		}

		return true;
	} catch (error) {
		console.error('Error deleting MOTD:', error);
		return false;
	}
}
